import { useQueryClient } from "@tanstack/react-query";
import { createContext, use, useCallback, useEffect, useState, type ReactNode } from "react";
import { getCurrentUser } from "aws-amplify/auth";
import { confirmSignIn, confirmSignUp, signIn, signOut, signUp } from "./amplify.js";

type Status = "checking" | "signed-in" | "signed-out";

/**
 * A staff account created by an administrator arrives with a one-time password
 * and must choose its own before it can be used. Sign-in reports that rather
 * than failing, so the caller can collect the new password.
 */
export type SignInResult = "signed-in" | "new-password-required";

interface AuthValue {
  readonly status: Status;
  readonly signIn: (email: string, password: string) => Promise<SignInResult>;
  readonly completeNewPassword: (password: string) => Promise<void>;
  readonly signUp: (input: SignUpInput) => Promise<SignUpResult>;
  readonly confirm: (email: string, code: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

export interface SignUpInput {
  readonly email: string;
  readonly password: string;
  readonly name: string;
  readonly timezone: string;
}

export type SignUpResult = "confirm-required" | "signed-in";

const AuthContext = createContext<AuthValue | undefined>(undefined);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    getCurrentUser()
      .then(() => {
        if (!cancelled) setStatus("signed-in");
      })
      .catch(() => {
        if (!cancelled) setStatus("signed-out");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const doSignIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      // Amplify keeps a signed-in session in storage; signing in over the top
      // of one throws rather than switching user, which is exactly what
      // happens when demoing three roles in one browser.
      await signOut().catch(() => undefined);

      const result = await signIn({ username: email.trim(), password });

      if (!result.isSignedIn) {
        // Every staff account starts here: an administrator created it,
        // Cognito emailed a one-time password, and it cannot be used until
        // they have replaced it with one only they know.
        if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          return "new-password-required";
        }

        throw new Error(
          result.nextStep.signInStep === "CONFIRM_SIGN_UP"
            ? "Your email address has not been confirmed yet. Check your inbox for the code."
            : "Additional sign-in steps are required, which this demo does not support.",
        );
      }

      queryClient.clear();
      setStatus("signed-in");
      return "signed-in";
    },
    [queryClient],
  );

  const doCompleteNewPassword = useCallback(
    async (password: string) => {
      const result = await confirmSignIn({ challengeResponse: password });

      if (!result.isSignedIn) {
        throw new Error("That password was not accepted. Check the requirements and try again.");
      }

      queryClient.clear();
      setStatus("signed-in");
    },
    [queryClient],
  );

  const doSignUp = useCallback(async (input: SignUpInput): Promise<SignUpResult> => {
    await signOut().catch(() => undefined);

    const result = await signUp({
      username: input.email.trim(),
      password: input.password,
      options: {
        userAttributes: {
          email: input.email.trim(),
          name: input.name.trim(),
          // Detected in the browser and stored up front, so the very first
          // appointment time this person sees is already in their own zone.
          "custom:timezone": input.timezone,
        },
      },
    });

    return result.isSignUpComplete ? "signed-in" : "confirm-required";
  }, []);

  const doConfirm = useCallback(async (email: string, code: string) => {
    await confirmSignUp({ username: email.trim(), confirmationCode: code.trim() });
  }, []);

  const doSignOut = useCallback(async () => {
    await signOut();
    // Clear rather than invalidate: this cache holds one person's health
    // information, and it must not survive into the next session in the same
    // browser tab.
    queryClient.clear();
    setStatus("signed-out");
  }, [queryClient]);

  return (
    <AuthContext
      value={{
        status,
        signIn: doSignIn,
        completeNewPassword: doCompleteNewPassword,
        signUp: doSignUp,
        confirm: doConfirm,
        signOut: doSignOut,
      }}
    >
      {children}
    </AuthContext>
  );
}

export function useAuth(): AuthValue {
  const value = use(AuthContext);
  if (value === undefined) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
